import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Calendar, CheckSquare, AlertTriangle, MessageSquare, 
  Mail, Smartphone, ArrowLeft, Save, RefreshCw, Clock, 
  ChevronDown, ChevronUp, Settings, History
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { API } from '../config/api';
import { formatDateTime } from '../utils/dateFormat';

function ReminderSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedSection, setExpandedSection] = useState('events');

  useEffect(() => {
    fetchSettings();
    fetchLogs();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/reminder-settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load reminder settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API}/admin/reminder-logs?days=7&limit=50`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/reminder-logs/stats?days=30`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/reminder-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        toast.success('Reminder settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const triggerReminders = async () => {
    try {
      const response = await fetch(`${API}/admin/reminders/trigger`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Reminder processing triggered');
        setTimeout(fetchLogs, 5000); // Refresh logs after 5 seconds
      } else {
        toast.error('Failed to trigger reminders');
      }
    } catch (error) {
      console.error('Failed to trigger reminders:', error);
      toast.error('Failed to trigger reminders');
    }
  };

  const updateCategorySetting = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const toggleChannel = (category, channel) => {
    const currentChannels = settings[category]?.channels || [];
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter(c => c !== channel)
      : [...currentChannels, channel];
    updateCategorySetting(category, 'channels', newChannels);
  };

  const CategorySection = ({ category, title, icon: Icon, description, children }) => {
    const isExpanded = expandedSection === category;
    const categorySettings = settings?.[category] || {};
    
    return (
      <Card className="mb-4">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setExpandedSection(isExpanded ? null : category)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${categorySettings.enabled ? 'bg-[#80298F]/10 text-[#80298F]' : 'bg-gray-100 text-gray-400'}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={categorySettings.enabled}
                onCheckedChange={(checked) => updateCategorySetting(category, 'enabled', checked)}
                onClick={(e) => e.stopPropagation()}
              />
              {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
            </div>
          </div>
        </CardHeader>
        {isExpanded && categorySettings.enabled && (
          <CardContent className="border-t pt-4">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} onLogout={onLogout} />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-[#80298F]" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Bell className="h-6 w-6 text-[#80298F]" />
                Reminder Settings
              </h1>
              <p className="text-gray-500">Configure automated client reminders</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={triggerReminders}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Test Now
            </Button>
            <Button onClick={saveSettings} disabled={saving} className="bg-[#80298F] hover:bg-[#80298F]/90">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="space-y-4">
            {/* Stats Summary */}
            {stats && (
              <Card className="mb-6">
                <CardContent className="py-4">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#80298F]">{stats.total_sent}</p>
                      <p className="text-sm text-gray-500">Total Sent (30d)</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{stats.successful}</p>
                      <p className="text-sm text-gray-500">Successful</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                      <p className="text-sm text-gray-500">Failed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">
                        {stats.total_sent > 0 ? Math.round((stats.successful / stats.total_sent) * 100) : 0}%
                      </p>
                      <p className="text-sm text-gray-500">Success Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Event Reminders */}
            <CategorySection
              category="events"
              title="Calendar Event Reminders"
              icon={Calendar}
              description="Remind clients about upcoming appointments, hearings, and meetings"
            >
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Reminder Intervals (days before event)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[14, 7, 3, 1, 0].map(day => (
                      <button
                        key={day}
                        onClick={() => {
                          const intervals = settings.events?.intervals || [];
                          const newIntervals = intervals.includes(day)
                            ? intervals.filter(d => d !== day)
                            : [...intervals, day].sort((a, b) => b - a);
                          updateCategorySetting('events', 'intervals', newIntervals);
                        }}
                        className={`px-3 py-1 rounded-full text-sm ${
                          settings.events?.intervals?.includes(day)
                            ? 'bg-[#80298F] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {day === 0 ? 'Day of' : `${day}d`}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Channels</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.events?.channels?.includes('email')}
                        onChange={() => toggleChannel('events', 'email')}
                        className="rounded border-gray-300 text-[#80298F] focus:ring-[#80298F]"
                      />
                      <Mail className="h-4 w-4" />
                      Email
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.events?.channels?.includes('sms')}
                        onChange={() => toggleChannel('events', 'sms')}
                        className="rounded border-gray-300 text-[#80298F] focus:ring-[#80298F]"
                      />
                      <Smartphone className="h-4 w-4" />
                      SMS
                    </label>
                  </div>
                </div>
              </div>
            </CategorySection>

            {/* Upcoming Task Reminders */}
            <CategorySection
              category="upcoming_tasks"
              title="Upcoming Task Reminders"
              icon={CheckSquare}
              description="Remind clients about tasks approaching their due date"
            >
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Reminder Intervals (days before due)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[7, 5, 3, 2, 1].map(day => (
                      <button
                        key={day}
                        onClick={() => {
                          const intervals = settings.upcoming_tasks?.intervals || [];
                          const newIntervals = intervals.includes(day)
                            ? intervals.filter(d => d !== day)
                            : [...intervals, day].sort((a, b) => b - a);
                          updateCategorySetting('upcoming_tasks', 'intervals', newIntervals);
                        }}
                        className={`px-3 py-1 rounded-full text-sm ${
                          settings.upcoming_tasks?.intervals?.includes(day)
                            ? 'bg-[#80298F] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {day}d
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Channels</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.upcoming_tasks?.channels?.includes('email')}
                        onChange={() => toggleChannel('upcoming_tasks', 'email')}
                        className="rounded border-gray-300 text-[#80298F] focus:ring-[#80298F]"
                      />
                      <Mail className="h-4 w-4" />
                      Email
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.upcoming_tasks?.channels?.includes('sms')}
                        onChange={() => toggleChannel('upcoming_tasks', 'sms')}
                        className="rounded border-gray-300 text-[#80298F] focus:ring-[#80298F]"
                      />
                      <Smartphone className="h-4 w-4" />
                      SMS
                    </label>
                  </div>
                </div>
              </div>
            </CategorySection>

            {/* Overdue Task Reminders */}
            <CategorySection
              category="overdue_tasks"
              title="Overdue Task Reminders"
              icon={AlertTriangle}
              description="Persistently remind clients about overdue tasks"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Initial Delay (days after due)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={settings.overdue_tasks?.initial_delay || 1}
                      onChange={(e) => updateCategorySetting('overdue_tasks', 'initial_delay', parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Repeat Every (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={settings.overdue_tasks?.repeat_interval || 2}
                      onChange={(e) => updateCategorySetting('overdue_tasks', 'repeat_interval', parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Max Reminders</Label>
                    <Input
                      type="number"
                      min="1"
                      max="60"
                      value={settings.overdue_tasks?.max_reminders || 30}
                      onChange={(e) => updateCategorySetting('overdue_tasks', 'max_reminders', parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Channels</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.overdue_tasks?.channels?.includes('email')}
                        onChange={() => toggleChannel('overdue_tasks', 'email')}
                        className="rounded border-gray-300 text-[#80298F] focus:ring-[#80298F]"
                      />
                      <Mail className="h-4 w-4" />
                      Email
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.overdue_tasks?.channels?.includes('sms')}
                        onChange={() => toggleChannel('overdue_tasks', 'sms')}
                        className="rounded border-gray-300 text-[#80298F] focus:ring-[#80298F]"
                      />
                      <Smartphone className="h-4 w-4" />
                      SMS (after 3 days)
                    </label>
                  </div>
                </div>
              </div>
            </CategorySection>

            {/* Unread Message Reminders */}
            <CategorySection
              category="unread_messages"
              title="Unread Message Reminders"
              icon={MessageSquare}
              description="Remind clients about messages they haven't read"
            >
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Reminder Intervals (hours after message sent)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[24, 48, 72, 168].map(hours => (
                      <button
                        key={hours}
                        onClick={() => {
                          const intervals = settings.unread_messages?.intervals || [];
                          const newIntervals = intervals.includes(hours)
                            ? intervals.filter(h => h !== hours)
                            : [...intervals, hours].sort((a, b) => a - b);
                          updateCategorySetting('unread_messages', 'intervals', newIntervals);
                        }}
                        className={`px-3 py-1 rounded-full text-sm ${
                          settings.unread_messages?.intervals?.includes(hours)
                            ? 'bg-[#80298F] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {hours < 48 ? `${hours}h` : `${hours / 24}d`}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Max Reminders</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={settings.unread_messages?.max_reminders || 3}
                    onChange={(e) => updateCategorySetting('unread_messages', 'max_reminders', parseInt(e.target.value))}
                    className="w-24 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Channels</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.unread_messages?.channels?.includes('email')}
                        onChange={() => toggleChannel('unread_messages', 'email')}
                        className="rounded border-gray-300 text-[#80298F] focus:ring-[#80298F]"
                      />
                      <Mail className="h-4 w-4" />
                      Email Only (for privacy)
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">SMS is disabled for unread messages to protect client privacy</p>
                </div>
              </div>
            </CategorySection>

            {/* Quiet Hours Notice */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Quiet Hours Active</p>
                    <p className="text-sm text-blue-700">
                      SMS reminders are not sent between 8:00 PM and 8:00 AM to respect client privacy.
                      Email reminders are sent at any time.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Recent Reminder Activity</CardTitle>
                <CardDescription>Last 7 days of reminder logs</CardDescription>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No reminders sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {logs.map((log, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          log.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${log.success ? 'bg-green-100' : 'bg-red-100'}`}>
                            {log.channel === 'email' ? (
                              <Mail className={`h-4 w-4 ${log.success ? 'text-green-600' : 'text-red-600'}`} />
                            ) : (
                              <Smartphone className={`h-4 w-4 ${log.success ? 'text-green-600' : 'text-red-600'}`} />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {log.reminder_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                            <p className="text-xs text-gray-500">
                              {log.client_name || log.client_email} • {formatDateTime(log.sent_at)}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          log.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {log.success ? 'Sent' : 'Failed'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default ReminderSettings;
